import { useEffect, useMemo, useState } from 'react';
import { getThemeObject } from '@logz-ui/styleguide/components/themes';
import { Notification, Spinner } from '@logz-ui/styleguide';
import { AppProvider } from '../AppProvider';
import { bootstrap } from '../../../app/bootstrap';
import { redirectNewAccountToNewApp } from '../../../hooks/new-application-redirect/redirect-new-account';
import { GlobalStyling } from './GlobalStyling';
import { App } from './App';
import { useSessionState } from 'ui/state/hooks';
import '../../../app/app.less';
import { useInitSideNavigation } from 'ui/hooks/init-side-navigation.hook';
import { useHistoryTracker } from 'ui/state/hooks/recent-visits.state.hook';
import { useTabVisibility } from 'ui/hooks/tabVisibility.hook';
import { useMarketplaceSuccessMessageActivation } from 'ui/hooks/marketplace-activation.hook';
import { initIDBRetentionWorker } from 'services/web-workers/init-idb-retention-worker';
import { initOPFSWorker } from 'services/web-workers/opfs/init-opfs-worker';
import { loginFlow } from 'core/auth-flows';

export const AppResolver: React.FC = () => {
  const [appReady, setAppReady] = useState<boolean>(false);

  const { userSettings } = useSessionState();
  const theme = useMemo(() => {
    return getThemeObject(userSettings?.logzioTheme);
  }, [userSettings?.logzioTheme]);

  useInitSideNavigation();
  useHistoryTracker();
  useTabVisibility();
  useMarketplaceSuccessMessageActivation(appReady);

  useEffect(() => {
    initIDBRetentionWorker();
    initOPFSWorker();

    (async () => {
      try {
        await bootstrap();

        await redirectNewAccountToNewApp();

        await loginFlow();
      } catch (err) {
        console.error('Error during app bootstrap:', err);
      } finally {
        setAppReady(true);
      }
    })();
  }, []);

  if (!appReady) {
    return (
      <>
        <GlobalStyling />
        <AppProvider theme={theme}>
          <Spinner fullscreen />
        </AppProvider>
      </>
    );
  }

  return (
    <>
      <GlobalStyling />
      <AppProvider theme={theme}>
        <App />
        <Notification />
      </AppProvider>
    </>
  );
};
